import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import {
  LabelToConceptId,
  PayrollPayrollSummaryImport,
} from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/types'
import { ResultMatches } from 'modules/payroll_bundle/components/common/helper/worksheet'
import { ImportData } from 'modules/payroll_bundle/lib/PayrollResultContext'

import Table from './_Table'
import { isValid } from './_Table/RowValidations'
import useBuildImportedEmployees from './useBuildImportedEmployees'

interface Props {
  currency: string
  worksheetMatches: ResultMatches
  payrollSummaryImport: PayrollPayrollSummaryImport
  payrollPolicyPeriodId: number
  labelToConceptId: LabelToConceptId
  onUpdateData: (importedData: {
    importedData: ImportData[]
    employeesSelected: Set<number>
  }) => void
}

// This component is a copy of a similar one created some time ago. If someone want to checks the
// blame of the file, please check this file
// https://github.com/factorialco/factorial/blob/ea3fa5b19eab385a8aab2b636193c6ba0baa50d8/frontend/src/modules/payroll_bundle/pages/SupplementsPage/BulkImport/_EmployeeMatcher/Matcher.tsx
const Matcher = ({
  currency,
  labelToConceptId,
  payrollSummaryImport,
  payrollPolicyPeriodId,
  onUpdateData,
}: Props) => {
  const [importedEmployees, setImportedEmployees] = useState<ImportData[]>([])
  const [importEmployeeIdToEmployeeId, setImportEmployeeIdToEmployeeId] = useState<
    Record<string, number>
  >({})
  const [employeesSelected, setEmployeesSelected] = useState<Set<number>>(new Set())

  const { importedEmployees: importedEmployeesData, loading: loadingImportedEmployees } =
    useBuildImportedEmployees({
      payrollImportId: payrollSummaryImport.id,
      payrollPolicyPeriodId,
      labelToConceptId,
    })

  useUnsafeEffect(
    () => {
      if (!loadingImportedEmployees) {
        setImportedEmployees(importedEmployeesData)
        onUpdateData({
          importedData: importedEmployeesData,
          employeesSelected: new Set(),
        })
        selectAll(importedEmployeesData)
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingImportedEmployees, importedEmployeesData]
  )

  const canBeSelected = (employees?: ImportData[]): number[] => {
    const selectables = (employees || importedEmployees).filter((importedEmployee) =>
      isValid(importedEmployee)
    )

    return selectables.map((e) => e.id)
  }
  const onSelectAll = () => {
    !employeesSelected.size ? selectAll(importedEmployees) : setEmployeesSelected(new Set([]))
  }

  const selectAll = (employees?: ImportData[]) => {
    const ids = canBeSelected(employees)
    setEmployeesSelected(new Set(ids))
    onUpdateData({
      importedData: importedEmployeesData,
      employeesSelected: new Set(ids),
    })
  }

  const onSelectResultItem = (importedEmployee: ImportData) => {
    const id = importedEmployee.id
    employeesSelected.has(id) ? onUnselect(id) : onSelect(id)
  }

  const onSelect = (userId: number) => {
    employeesSelected.add(userId)
    setEmployeesSelected(new Set([...employeesSelected]))
    onUpdateData({
      importedData: importedEmployeesData,
      employeesSelected: new Set([...employeesSelected]),
    })
  }

  const onUnselect = (userId: number) => {
    employeesSelected.delete(userId)
    setEmployeesSelected(new Set([...employeesSelected]))
    onUpdateData({
      importedData: importedEmployeesData,
      employeesSelected: new Set([...employeesSelected]),
    })
  }

  const onUnAssign = async (employeeId: number | null, resultItemId: number) => {
    if (employeeId === null) {
      return
    }

    const importedEmployee = importedEmployees.find((item) => item.id === resultItemId)

    if (!importedEmployee) {
      return
    }

    importedEmployee.employee_id = null
    importedEmployee.assigned_date = null

    const importEmployeeId = importedEmployee.summaryImportEmployeeId
      ? String(importedEmployee.summaryImportEmployeeId)
      : ''

    const updatedImportEmployeeIdToEmployeeId = {
      ...importEmployeeIdToEmployeeId,
    }

    delete updatedImportEmployeeIdToEmployeeId[importEmployeeId]

    setImportedEmployees([...importedEmployees])
    setImportEmployeeIdToEmployeeId(updatedImportEmployeeIdToEmployeeId)
    onUpdateData({
      importedData: importedEmployeesData,
      employeesSelected: new Set([...employeesSelected]),
    })

    track(trackingEvents.payroll.manuallyUnAssignEmployeeToResultItem, {
      employeeId: employeeId,
    })
  }

  const onAssign = async (employeeId: number | null, resultItemId: number) => {
    if (employeeId === null) {
      return
    }

    const importedEmployee = importedEmployees.find((item) => item.id === resultItemId)

    if (!importedEmployee) {
      return
    }

    importedEmployee.employee_id = employeeId
    // Added so we can sort them later on by most recent assigned
    importedEmployee.assigned_date = new Date().getTime()

    const importEmployeeId = importedEmployee.summaryImportEmployeeId
      ? String(importedEmployee.summaryImportEmployeeId)
      : ''

    const updatedImportEmployeeIdToEmployeeId = {
      ...importEmployeeIdToEmployeeId,
      [importEmployeeId]: employeeId,
    }

    setImportedEmployees([...importedEmployees])
    setImportEmployeeIdToEmployeeId(updatedImportEmployeeIdToEmployeeId)
    onUpdateData({
      importedData: importedEmployeesData,
      employeesSelected: new Set([...employeesSelected]),
    })

    track(trackingEvents.payroll.manuallyAssignEmployeeToResultItem, {
      employeeId: employeeId,
    })
  }

  return (
    <Box flexDirection="column" alignItems="center" overflowY="auto">
      {payrollSummaryImport.compensationType && (
        <Table
          checkable
          onSelectAll={onSelectAll}
          selected={employeesSelected}
          selectable={canBeSelected()}
          onSelectItem={onSelectResultItem}
          onUnAssignEmployee={onUnAssign}
          onAssignEmployee={onAssign}
          loading={loadingImportedEmployees}
          currency={currency}
          payrollImportId={payrollSummaryImport.id}
          payrollPolicyPeriodId={payrollPolicyPeriodId}
          labelToConceptId={labelToConceptId}
          compensationType={payrollSummaryImport.compensationType}
          importedData={importedEmployees}
        />
      )}
    </Box>
  )
}

export default observer(Matcher)
